import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

export default function ASHero() {
  const { ref: badgeRef, revealed: badgeRevealed } = useScrollReveal({ delay: 100 });
  const { ref: headlineRef, revealed: headlineRevealed } = useScrollReveal({ delay: 250 });
  const { ref: subtitleRef, revealed: subtitleRevealed } = useScrollReveal({ delay: 400 });
  const { ref: btnsRef, revealed: btnsRevealed } = useScrollReveal({ delay: 550 });
  const { containerRef: statsRef, revealedCount: statsRevealed } = useScrollRevealGroup<HTMLDivElement>(4);

  return (
    <section className="relative w-full min-h-screen flex items-center bg-background-50 overflow-hidden">
      {/* Точечная сетка */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, oklch(var(--primary-500)) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Оранжевые свечения */}
      <div className="absolute top-20 right-0 w-[600px] h-[600px] opacity-[0.06]"
        style={{ background: 'radial-gradient(circle at center, oklch(var(--primary-500)), transparent 70%)' }}
      />
      <div className="absolute bottom-10 left-0 w-[500px] h-[500px] opacity-[0.04]"
        style={{ background: 'radial-gradient(circle at center, oklch(var(--primary-500)), transparent 70%)' }}
      />

      {/* Tech-ноды */}
      <div className="absolute top-1/3 right-[12%] hidden lg:block">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border border-primary-500/20 animate-ping" style={{ animationDuration: '3s' }} />
          <div className="absolute inset-2 rounded-full border border-primary-500/30" />
          <div className="absolute inset-[30%] rounded-full bg-primary-500/10 flex items-center justify-center">
            <i className="ri-robot-2-line text-primary-500 text-lg"></i>
          </div>
        </div>
      </div>
      <div className="absolute bottom-1/4 left-[8%] hidden lg:block">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border border-primary-500/15 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
          <div className="absolute inset-[25%] rounded-full bg-primary-500/5 flex items-center justify-center">
            <i className="ri-brain-line text-primary-400 text-base"></i>
          </div>
        </div>
      </div>
      <div className="absolute top-1/2 right-[5%] hidden xl:block w-px h-32 bg-gradient-to-b from-transparent via-primary-300/30 to-transparent" />
      <div className="absolute bottom-1/3 left-[18%] hidden xl:block w-24 h-px bg-gradient-to-r from-transparent via-primary-300/20 to-transparent" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 pt-28 pb-12 flex flex-col items-start">
        {/* Бейдж */}
        <div ref={badgeRef} className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200/60 rounded-full mb-8 reveal-fade-up ${badgeRevealed ? 'revealed' : ''}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
          Продукт AIGAM
        </div>

        <h1 ref={headlineRef} className={`font-display font-extrabold text-[44px] md:text-[64px] leading-[1.05] tracking-tightest text-foreground-950 max-w-3xl reveal-fade-up ${headlineRevealed ? 'revealed' : ''}`}>
          AI-рассыльщик сам ведёт
          <br />
          диалоги и доводит
          <br />
          до заявки
        </h1>

        <p ref={subtitleRef} className={`mt-6 md:mt-8 text-[17px] md:text-[19px] text-foreground-500 max-w-xl leading-relaxed reveal-fade-up ${subtitleRevealed ? 'revealed' : ''}`}>
          AI-рассыльщик AIGAM автоматически общается с клиентами через ваши мессенджеры — пишет первым, отвечает на вопросы, квалифицирует лидов и передаёт готовые сделки в CRM.
        </p>

        <div ref={btnsRef} className={`mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 reveal-fade-up ${btnsRevealed ? 'revealed' : ''}`}>
          <a
            href="#as-pricing"
            className="h-12 px-7 inline-flex items-center justify-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-[15px] font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            Подключить AI
          </a>
          <a
            href="#as-how"
            className="h-12 px-6 inline-flex items-center justify-center rounded-md border border-background-200 hover:border-background-300 hover:bg-background-100 text-foreground-700 text-[15px] font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Как это работает
          </a>
        </div>

        {/* Статистика */}
        <div ref={statsRef} className="mt-16 md:mt-20 w-full max-w-3xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0 md:divide-x divide-background-200/70">
            {[
              { value: '< 3 сек', label: 'Ответ AI', icon: 'ri-flashlight-line' },
              { value: '24/7', label: 'Без выходных', icon: 'ri-24-hours-line' },
              { value: '98%', label: 'Точность ответов', icon: 'ri-focus-3-line' },
              { value: '×3.5', label: 'Рост заявок', icon: 'ri-line-chart-line' },
            ].map((stat, i) => (
              <div key={stat.label} className={`text-center px-4 py-3 reveal-fade-up ${i < statsRevealed ? 'revealed' : ''}`} style={i < statsRevealed ? { transitionDelay: `${i * 100}ms` } : undefined}>
                <span className="w-8 h-8 mx-auto mb-2 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                  <i className={`${stat.icon} text-sm`}></i>
                </span>
                <div className="text-[20px] font-display font-extrabold text-foreground-950">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}